import React, { useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { get, noop } from "lodash";
import ApolloContext from "@tvg/utils/apolloContext";
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
import { useSelector, useDispatch } from "react-redux";
import { RaceProgram, Promo } from "@tvg/ts-types/Race";
// Utils
import getOptedInPromos from "@tvg/utils/PromoUtils";
import { openExternalLink } from "@tvg/utils/mobileUtils";
import RaceHdrUtils from "@tvg/utils/RaceHdrUtils";
import { getIsLogged } from "@urp/store-selectors";
import {
  Icon,
  TooltipDescriptive,
  Mtp,
  useMediaQuery,
  breakpoints
} from "@tvg/design-system";
import {
  CurrentRaceContainer,
  RaceDetailsContainer,
  TitleWrapper,
  HeaderContainer,
  ButtonsContainer,
  ActiveBetsButtonContainer
} from "./styled-components";
import {
  getIsHamburgerMenuOpen,
  getOldPromosURLs,
  getOptedInPromosStore,
  getTrackHeaderEnhacement
} from "../../../store/selectors";
import { setIsPoolsNInfoModalOpen } from "../../../store/actions";
import { useCurrentRaceWagers, useIsLiveTv } from "../../../hooks";
import { getPromoUrl, isPromoExternal } from "../../../utils/promos";
import ActionButtons from "./ActionButtons";
import AuxiliaryModulesButtons from "./AuxiliaryModulesButtons";
import RaceDetailsList from "./RaceDetailsList";
import RaceTitle from "./RaceTitle";
import ActiveBetsButton from "../ActiveBetsButton";

interface CurrentRaceProps {
  currentRace: RaceProgram;
  races?: RaceProgram[];
  mainCurrentRace?: RaceProgram;
  leg?: number;
  isPickHeader?: boolean;
  enableCompactView?: boolean;
  isRacecardScrollable?: boolean;
  withVideo?: boolean;
  isOnModal?: boolean;
  onClickActiveBetsButton?: () => void;
  onClickWatchReplay?: () => void;
  renderSelectAll?: () => React.ReactElement;
}

export const CurrentRaceDetails: React.FC<CurrentRaceProps> = ({
  currentRace,
  races,
  mainCurrentRace,
  leg,
  isPickHeader,
  isRacecardScrollable,
  enableCompactView,
  withVideo,
  isOnModal = false,
  onClickActiveBetsButton,
  onClickWatchReplay = noop,
  renderSelectAll
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLiveTv = useIsLiveTv();
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const isHamburgerMenuOpen = useSelector(getIsHamburgerMenuOpen);
  const optedInPromos = useSelector(getOptedInPromosStore);
  const isLogged = useSelector(getIsLogged);
  const oldPromosURLs = useSelector(getOldPromosURLs);
  const hasTrackHeaderEnhacement = useSelector(getTrackHeaderEnhacement);

  const { behgClient } = useContext<ApolloContextClients>(ApolloContext);
  const { raceWagersSummary, raceWagers } = useCurrentRaceWagers({
    race:
      isPickHeader && !isRacecardScrollable && mainCurrentRace
        ? mainCurrentRace
        : currentRace,
    behgClient
  });

  const { raceNumber, track, description } = currentRace;

  const raceDetails = RaceHdrUtils.createRaceDataForHeader(currentRace, true);

  const promos: Promo | null = getOptedInPromos(
    get(currentRace, "promos", []),
    optedInPromos,
    isLogged
  );

  const isExternal = isPromoExternal({ promos, oldPromosURLs });

  const onClickPromo = useCallback(() => {
    if (promos) {
      const promoUrl = getPromoUrl(isExternal, promos, currentRace, isMobile);

      if (!isMobile && typeof window !== "undefined") {
        // eslint-disable-next-line security/detect-non-literal-fs-filename
        window.open(promoUrl, "_blank", "noopener,noreferrer");
      } else if (isExternal) {
        openExternalLink(promoUrl);
      } else {
        navigate(promoUrl);
      }
    }
  }, [promos, isExternal, currentRace, isMobile]);

  const onClickPoolsInfo = useCallback(() => {
    dispatch(
      setIsPoolsNInfoModalOpen({
        isPoolsNInfoModalOpen: true,
        PIRace: currentRace
      })
    );
  }, [currentRace]);

  const enableEnhacedButtons = hasTrackHeaderEnhacement && isMobile;

  const enableLiveHeader = useMemo(
    () => enableEnhacedButtons || (isLiveTv && !enableCompactView),
    [isLiveTv, enableCompactView, hasTrackHeaderEnhacement]
  );

  const isMultileg = isPickHeader || isRacecardScrollable;
  const showActionButtons = !isMultileg || (isMultileg && leg === 1);
  const hasWagers = raceWagers.length > 0;

  return (
    <CurrentRaceContainer
      data-qa-label="race-content-container"
      isPickHeader={isPickHeader}
      isRacecardScrollable={isRacecardScrollable}
    >
      <HeaderContainer>
        {isMobile ? (
          <RaceDetailsContainer>
            <RaceTitle
              isLiveTv={isLiveTv && !enableCompactView}
              country={track?.trackLocation.country}
              trackName={track?.trackName}
              raceNumber={raceNumber}
              showLegNumber={false}
              legNumber={leg}
            />
            <RaceDetailsList
              isLogged={isLogged}
              raceDetails={raceDetails}
              hasStartSeparator
            />
          </RaceDetailsContainer>
        ) : (
          <>
            <TitleWrapper>
              {enableLiveHeader && !isMultileg && (
                <Mtp
                  mtp={get(currentRace, "mtp", 0)}
                  status={get(currentRace, "status.code", "")}
                  postTime={get(currentRace, "postTime", "")}
                  qaLabel="mtp-status"
                />
              )}

              <RaceTitle
                isLiveTv={isLiveTv && !enableCompactView}
                country={track?.trackLocation.country}
                trackName={track?.trackName}
                raceNumber={raceNumber}
                showLegNumber={isRacecardScrollable}
                legNumber={leg}
              />
            </TitleWrapper>
            <RaceDetailsContainer>
              <RaceDetailsList isLogged={isLogged} raceDetails={raceDetails} />
              {description && !isHamburgerMenuOpen && (
                <TooltipDescriptive
                  header={`${track?.trackName!} Details`}
                  body={description!}
                  qaLabel="tooltip-race-info"
                  placement="bottom-left"
                  layer={0}
                  onAction="hover"
                >
                  <Icon
                    name="info"
                    size="s"
                    qaLabel="current-race-info"
                    ml="space-1"
                  />
                </TooltipDescriptive>
              )}
            </RaceDetailsContainer>
          </>
        )}
      </HeaderContainer>

      <ButtonsContainer hidden={isOnModal && isMobile}>
        {enableLiveHeader && showActionButtons && (
          <ActionButtons
            race={currentRace}
            races={races}
            isMultileg={isMultileg}
            isOptedIn={promos?.isOptedIn}
            onClickPromo={onClickPromo}
            onClickWatchReplay={onClickWatchReplay}
            hasPromo={!!promos}
            withVideo={withVideo}
            hasWagers={raceWagers.length > 0}
            hasReplay={
              currentRace.video?.hasReplay &&
              !!currentRace.video?.replayFileName
            }
          />
        )}

        {isMobile &&
          (enableLiveHeader ? (
            <AuxiliaryModulesButtons
              hasWagers={hasWagers}
              raceWagersSummary={raceWagersSummary}
              onClickPoolsInfo={onClickPoolsInfo}
              onClickBetsButton={onClickActiveBetsButton}
            />
          ) : (
            hasWagers &&
            onClickActiveBetsButton && (
              <ActiveBetsButtonContainer>
                <ActiveBetsButton
                  iconPosition="start"
                  totalGambledCount={raceWagersSummary.totalGambledCount}
                  onClick={onClickActiveBetsButton}
                />
              </ActiveBetsButtonContainer>
            )
          ))}

        {typeof renderSelectAll === "function" && renderSelectAll()}
      </ButtonsContainer>
    </CurrentRaceContainer>
  );
};

export default CurrentRaceDetails;
